import { HttpClient } from '@angular/common/http'
import { inject, Injectable, InjectionToken, Signal, Type } from '@angular/core'
import { Route, Router } from '@angular/router'
import { Observable, tap } from 'rxjs'
import { DashboardConfig } from '../models/dashboardConfig'
import { AuthGuard } from './auth-guard.service'
import { BROWSER_STORAGE, CachedReferenceService } from '../../public/reference-data/base-reference.service'
import {
  PublishedDetailsReviewDashboardComponent,
} from '../../public/dashboard/published-details-review-dashboard.component'
import { DefaultDashboardComponent } from '../../public/dashboard/default-dashboard.component'
import { toSignal } from '@angular/core/rxjs-interop'

export const dashboardRouteDataKey = 'dashboard'

const CACHED_DASHBOARD_DATA = new InjectionToken<CachedReferenceService<DashboardConfig>>(
  'dashboards',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService<DashboardConfig>(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'dashboard',
      undefined,
      undefined,
      [{ name: 'name' }],
    ),
  },
)

const customDashboardComponents: Readonly<Record<string, Type<unknown>>> = {
  published_details_review_published_details_review: PublishedDetailsReviewDashboardComponent,
}

const knownNavGroups: Readonly<Record<string, string>> = {
  'Settlement Process': 'settlement-process',
  'Data Exploration': 'data-exploration',
  'Data Management': 'data-management',
  'System Configuration': 'system-configuration',
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly router = inject(Router)
  private readonly dashboards = inject(CACHED_DASHBOARD_DATA)

  readonly dashboardsSignal: Signal<readonly DashboardConfig[]> = toSignal(this.dashboards.get(), { initialValue: [] })

  constructor() {
    this.dashboards.init()
  }

  private getDashboardRoute(dashboard: DashboardConfig): Route {
    return {
      path: DashboardService.getDashboardUrlParts([dashboard], dashboard.name).filter(Boolean).join('/'),
      component: customDashboardComponents[dashboard.name] ?? DefaultDashboardComponent,
      canActivate: [AuthGuard],
      resolve: { [dashboardRouteDataKey]: () => dashboard },
    }
  }

  get(): Observable<readonly DashboardConfig[]> {
    return this.dashboards.get().pipe(
      tap((dashboards) => {
        this.router.resetConfig([
          ...this.router.config, ...dashboards.map(_ => this.getDashboardRoute(_)),
        ])
      }),
    )
  }

  findDashboard(path: string): DashboardConfig | undefined {
    return this.dashboardsSignal().find(dashboard =>
      path.startsWith(DashboardService.getPathname(dashboard)),
    )
  }

  static getWidget = (
    dashboards: readonly DashboardConfig[],
    dashboardName: string,
    widgetFriendly: string,
  ) => {
    const dashboard = dashboards.find((d) => d.name === dashboardName)
    if (!dashboard) {
      return widgetFriendly
    }

    const param = dashboard.params.find(
      (p) => p.widget_friendly === widgetFriendly,
    )
    if (!param) {
      return widgetFriendly
    }

    return param.widget
  }

  static getUrlParts({ nav_group, name }: DashboardConfig): readonly string[] {
    const navGroup = nav_group && knownNavGroups[nav_group]
    return navGroup ? [navGroup, name] : [name]
  }

  static getPathname(dashboard: DashboardConfig): string {
    return `/${DashboardService.getUrlParts(dashboard).join('/')}`
  }

  static getDashboardUrlParts (dashboards: readonly DashboardConfig[],  dashboardName: string): readonly string[] {
    const dashboard = dashboards.find(_ => _.name === dashboardName)
    return dashboard ? DashboardService.getUrlParts(dashboard) : [dashboardName]
  }
}
